import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSelector } from 'react-redux';

import { bookTickets } from '@/api/payments';
import Button from '@/components/elements/button';
import SeatSchemaForm from '@/components/event-payment/seat-schema-form';
import SubmitForm from '@/components/event-payment/submit-form';
import TicketForm from '@/components/event-payment/ticket-form';
import { FormSlide } from '@/components/event-payment/types';
import EventBookingResult from '@/components/payment-result/event-booking-result';
import { useTranslation } from '@/i18n/client';
import { selectSelectedTickets } from '@/store/event-payment/event-payment.selectotrs';
import { EventDetails } from '@/types/events';
import { CurrencyCode } from '@/types/general/base';
import { WithLocale } from '@/types/platform-data';
import { TicketsBookInput, TicketSelectOption } from '@/types/tickets';
import { recaptchaSiteKey } from '@/utils/constants';

interface Props {
    ticketOptions: TicketSelectOption[];
    isVisible: boolean;
    event: EventDetails;
    currencyCode: CurrencyCode;
    onClose: () => void;
}

export default function EventPaymentForm(props: WithLocale<Props>) {
    const selectedTickets = useSelector(selectSelectedTickets);
    const [formSlide, setFormSlide] = useState<FormSlide>(FormSlide.ticket);
    const formRef = useRef<HTMLDivElement>(null);

    const { t: tCo } = useTranslation(props.lang, 'common');

    const [bookingResult, setBookingResult] = useState<any | null>(null);

    useEffect(() => {
        formSlide !== FormSlide.ticket && formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [formSlide]);

    const backHandler = () => {
        if (formSlide === FormSlide.result) {
            setFormSlide(FormSlide.submit);
        }
        if (formSlide === FormSlide.submit) {
            setFormSlide(FormSlide.ticket);
        }
    };

    const handlePayment = async (formData: TicketsBookInput) => {
        const response = await bookTickets(formData);
        setBookingResult(response.purchaseTransactionId ? response : null);
        setFormSlide(FormSlide.result);
    };

    return (
        <div
            ref={formRef}
            className={`ease relative z-[1] mt-20 flex w-full flex-col flex-nowrap overflow-hidden border border-solid border-gray-300 bg-gray-50 transition-all duration-200 ${props.isVisible ? 'max-h-[1000px]' : 'max-h-[0px]'}`}
        >
            <div className="flex items-center justify-between">
                {formSlide === FormSlide.submit && (
                    <div onClick={backHandler} className="ml-2 flex cursor-pointer items-center gap-2 py-1">
                        <span className="icon bg-arrow" />
                        <p className="text-sm md:text-lg">{tCo('navigation.back')}</p>
                    </div>
                )}
                <div
                    onClick={props.onClose}
                    className="ml-auto flex cursor-pointer items-center justify-center border border-solid border-black p-2 md:p-4"
                >
                    <span className="icon h-3 w-3 rotate-180 cursor-pointer bg-extend"></span>
                </div>
            </div>
            <div className={`flex min-w-full flex-col gap-y-2 py-4`}>
                <div
                    id="ticket-form"
                    className={`relative flex min-w-full flex-nowrap transition-transform duration-200`}
                    style={{
                        transform: `translateX(${`-${formSlide * 100}%`})`,
                    }}
                >
                    {props.event.seat_schema ? (
                        <SeatSchemaForm
                            currencyCode={props.currencyCode}
                            event={props.event}
                            ticketOptions={props.ticketOptions}
                            lang={props.lang}
                        />
                    ) : (
                        <TicketForm
                            lang={props.lang}
                            ticketOptions={props.ticketOptions}
                            onSlide={() => setFormSlide(FormSlide.submit)}
                            currencyCode={props.currencyCode}
                        />
                    )}
                    <SubmitForm
                        isVisible={formSlide === FormSlide.submit}
                        onSubmit={handlePayment}
                        currencyCode={props.currencyCode}
                        onSlideBack={() => setFormSlide(FormSlide.ticket)}
                        lang={props.lang}
                        event={props.event}
                    />
                    <EventBookingResult
                        currencyCode={props.currencyCode}
                        event={props.event}
                        onSlideBack={() => setFormSlide(FormSlide.submit)}
                        lang={props.lang}
                        bookingResponse={bookingResult ?? null}
                    />
                </div>
                {formSlide === FormSlide.ticket && (
                    <div
                        onClick={() => selectedTickets.length > 0 && setFormSlide(FormSlide.submit)}
                        className={`md:h-20 ${selectedTickets.length > 0 ? 'cursor-pointer' : ''}`}
                    >
                        <Button
                            disabled={selectedTickets.length === 0}
                            className={`!fixed bottom-0 right-0 z-10 !h-20 w-full rounded-none transition-all duration-200 md:!absolute md:h-20 ${selectedTickets.length === 0 ? 'translate-x-full' : ''}`}
                            title={tCo('common.buy')}
                            onClick={() => setFormSlide(FormSlide.submit)}
                        />
                    </div>
                )}
            </div>

            <div>
                <ReCAPTCHA badge={'bottomright'} size={'invisible'} hl={props.lang} sitekey={recaptchaSiteKey} />
            </div>
        </div>
    );
}
